import L from 'leaflet';
import { useMemo } from 'react';
import { Marker } from 'react-leaflet';

// Draws marks above the vertex handles, so each mark stays visible while the operator edits.
const OUTSIDE_COORDINATE_Z_INDEX_OFFSET = 1000;

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// The shape name comes from the operator, so it is escaped before it goes into the icon HTML.
const createOutsideCoordinateIcon = (accessibleName) =>
  L.divIcon({
    className: 'alert-map-editor-outside-coordinate',
    html: `<span role="img" aria-label="${escapeHtml(accessibleName)}">!</span>`,
    iconSize: [20, 20],
    // The lower-left corner of the mark touches the point. The vertex handle stays visible.
    iconAnchor: [-2, 22],
  });

/**
 * Puts a red exclamation mark on each coordinate point outside the authorized jurisdiction.
 * The marks take no clicks, so a drag on the vertex handle under a mark still works. A mark goes
 * away when its point is inside again, because the parent recalculates the list on each change.
 *
 * @param {Object} props
 * @param {{ key: string, position: { lat: number, lng: number }, accessibleName: string }[]}
 * props.points The points outside the jurisdiction.
 * @returns {JSX.Element[]}
 */
const OutsideCoordinateMarkers = ({ points = [] }) => {
  const marks = useMemo(
    () =>
      points.map((outsidePoint) => ({
        ...outsidePoint,
        icon: createOutsideCoordinateIcon(outsidePoint.accessibleName),
      })),
    [points],
  );

  return marks.map((mark) => (
    <Marker
      // Leaflet reads the title only on creation, so a new name makes a new marker.
      key={`${mark.key}-${mark.accessibleName}`}
      position={mark.position}
      icon={mark.icon}
      title={mark.accessibleName}
      interactive={false}
      keyboard={false}
      zIndexOffset={OUTSIDE_COORDINATE_Z_INDEX_OFFSET}
    />
  ));
};

export default OutsideCoordinateMarkers;
